import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Lock } from "lucide-react";
import { api } from "../../lib/api";

// Sponsor Slots — manual availability ledger for the 20 exclusive ad
// slots (4 islands × 5 formats). Powers the live availability grid on
// the public /sponsor page. This tab is deliberately separate from
// Ad Slots (which manages creatives/targeting/dates for the ads
// collection) because the slot ledger only tracks "sold vs available"
// status — mixing them in one collection introduced avoidable risk
// (see 2026-02-27 Ad Slot Model Reconciliation).
//
// When Calvin closes the Oyster Bay deal, he taps St. Maarten →
// Leaderboard → Mark sold, drops a short note ("Oyster Bay · $200 LP
// through Dec 2026"), and the /sponsor page grid flips to Reserved
// live. No creative upload, no date-window logic; that comes later.

const FORMAT_LABEL = {
  leaderboard:        "Leaderboard · 728 × 90",
  sidebar_tall:       "Half-Page · 300 × 600",
  rectangle:          "Rectangle · 300 × 250",
  sidebar_sticky:     "Sidebar Sticky · 300 × 250",
  sponsored_category: "Sponsored Category",
};

const ISLAND_META = {
  aruba:     { flag: "🇦🇼", name: "Aruba" },
  curacao:   { flag: "🇨🇼", name: "Curaçao" },
  bonaire:   { flag: "🇧🇶", name: "Bonaire" },
  stmaarten: { flag: "🇸🇽", name: "St. Maarten" },
};

const ISLANDS = ["aruba", "curacao", "bonaire", "stmaarten"];
const FORMATS = Object.keys(FORMAT_LABEL);

export const SponsorSlotsTab = () => {
  const [slots, setSlots] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {island, format}
  const [editNote, setEditNote] = useState("");
  const [editSponsor, setEditSponsor] = useState("");
  const [editTermMonths, setEditTermMonths] = useState(3);
  const [saving, setSaving] = useState(false);
  // Sponsors already holding at least one Sold slot — powers the
  // "existing sponsor" quick-picks below the sponsor input so admins
  // stay on the same grouping key across a sponsor's 2nd, 3rd, etc.
  // slots. Without this, "Oyster Bay" vs "oyster bay" would be two
  // sponsors → wrong discount count.
  const [sponsors, setSponsors] = useState([]);
  // Config editor state — mirrors the four editable fields, only
  // pushed to the backend when the admin clicks Save. Keeps a
  // ref to the last-saved config so the field-changed indicator
  // is honest (highlights unsaved edits only).
  const [cfg, setCfg] = useState(null);
  const [cfgSaved, setCfgSaved] = useState(null);
  const [cfgSaving, setCfgSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, sp] = await Promise.all([
        api.get("/admin/sponsor/slots"),
        api.get("/admin/sponsor/config"),
        api.get("/admin/sponsors"),
      ]);
      setSlots(s.data.slots || []);
      setCfg(p.data);
      setCfgSaved(p.data);
      setSponsors(sp.data.sponsors || []);
      // Also fetch public /sponsor/pricing for the launch_partner_active
      // flag — that's not on the raw config doc since it's derived.
      const pp = await api.get("/sponsor/pricing");
      setPricing(pp.data);
    } catch (e) {
      toast.error("Failed to load sponsor slots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const findSlot = (island, format) =>
    slots.find((s) => s.island === island && s.format === format) || {
      island, format, status: "available", sold_note: "", sold_at: null,
    };

  const openEdit = (island, format) => {
    const cell = findSlot(island, format);
    setEditing({ island, format });
    setEditNote(cell.sold_note || "");
    setEditSponsor(cell.sponsor_name || "");
    setEditTermMonths(cell.term_months || 3);
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditNote("");
    setEditSponsor("");
    setEditTermMonths(3);
  };

  const setStatus = async (island, format, newStatus, note = "", sponsorName = "", termMonths = 3) => {
    if (newStatus === "sold" && !sponsorName.trim()) {
      toast.error("Sponsor name is required to mark a slot sold");
      return;
    }
    if (newStatus === "sold" && Number(termMonths) < 3) {
      toast.error("Minimum term is 3 months");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/sponsor/slots/${island}/${format}`, {
        status: newStatus,
        sold_note: note,
        sponsor_name: sponsorName,
        term_months: Number(termMonths) || 3,
      });
      toast.success(`${ISLAND_META[island].name} · ${FORMAT_LABEL[format].split(" ·")[0]} → ${newStatus}`);
      await load();
      cancelEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const soldCount = slots.filter((s) => s.status === "sold").length;
  const availableCount = slots.length - soldCount;

  const cfgDirty = cfg && cfgSaved && (
    Number(cfg.standard_price_usd) !== Number(cfgSaved.standard_price_usd) ||
    Number(cfg.launch_price_usd) !== Number(cfgSaved.launch_price_usd) ||
    cfg.launch_start !== cfgSaved.launch_start ||
    cfg.launch_end !== cfgSaved.launch_end
  );

  const saveConfig = async () => {
    if (!cfgDirty || !cfg) return;
    setCfgSaving(true);
    try {
      const r = await api.put("/admin/sponsor/config", {
        standard_price_usd: Number(cfg.standard_price_usd),
        launch_price_usd: Number(cfg.launch_price_usd),
        launch_start: cfg.launch_start,
        launch_end: cfg.launch_end,
      });
      setCfg(r.data);
      setCfgSaved(r.data);
      toast.success("Pricing updated — public /sponsor page now reflects the new rates.");
      // Refresh the derived pricing (launch_partner_active) too.
      const pp = await api.get("/sponsor/pricing");
      setPricing(pp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setCfgSaving(false);
    }
  };

  if (loading) {
    return <div className="text-[13px] text-[#6B7280]">Loading sponsor slots…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-sponsor-slots">
      {/* Header + pricing summary — reflects the locked model so the
          admin has a single-source-of-truth reference visible right
          next to the toggle grid. */}
      <div className="card-soft p-5">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={16} className="text-[#0AADA0]" />
          <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0]">
            Sponsor Slots · Exclusive Inventory
          </h3>
        </div>
        <p className="text-[12px] text-[#6B7280] mb-3">
          4 islands × 5 formats = 20 slots. One sponsor per format, per island.
          {pricing?.launch_partner_active
            ? <> Launch Partner rate is <b>active</b> — active until {cfgSaved?.launch_end}.</>
            : <> Launch Partner rate is <b>inactive</b> — past the {cfgSaved?.launch_end} cutoff.</>}
        </p>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0AADA0]/10 text-[#0AADA0] font-medium"
            data-testid="sponsor-slots-available-pill"
          >
            <CheckCircle2 size={12} />
            <span>{availableCount} available</span>
          </span>
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5F5F5] text-[#6B7280] font-medium"
            data-testid="sponsor-slots-sold-pill"
          >
            <span>{soldCount} sold</span>
          </span>
        </div>
      </div>

      {/* Editable pricing controls — writes to the
          `settings.sponsor_pricing` doc. Public /sponsor and
          /advertise reflect the change immediately (no redeploy).
          Slots already marked Sold keep their locked-in
          `sold_price_usd` snapshot regardless of edits here. */}
      {cfg && (
        <div className="card-soft p-5" data-testid="sponsor-config-editor">
          <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-1">
            Pricing settings
          </h3>
          <p className="text-[11px] text-[#6B7280] mb-4">
            Public rates shown on /sponsor. Sold slots keep their locked-in rate — edits here only affect new quotes.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">
                Standard monthly rate (USD)
              </span>
              <div className="flex items-center gap-1">
                <span className="text-[#6B7280]">$</span>
                <input
                  type="number"
                  min="0"
                  value={cfg.standard_price_usd ?? ""}
                  onChange={(e) => setCfg({ ...cfg, standard_price_usd: e.target.value })}
                  className="w-32 bg-[#FAFAFA] rounded-lg px-3 py-2 text-[#1A1A1A] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
                  data-testid="cfg-standard-price"
                />
                <span className="text-[12px] text-[#6B7280]">/month</span>
              </div>
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">
                Launch Partner rate (USD)
              </span>
              <div className="flex items-center gap-1">
                <span className="text-[#6B7280]">$</span>
                <input
                  type="number"
                  min="0"
                  value={cfg.launch_price_usd ?? ""}
                  onChange={(e) => setCfg({ ...cfg, launch_price_usd: e.target.value })}
                  className="w-32 bg-[#FAFAFA] rounded-lg px-3 py-2 text-[#1A1A1A] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
                  data-testid="cfg-launch-price"
                />
                <span className="text-[12px] text-[#6B7280]">/month</span>
              </div>
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">
                Launch Partner starts
              </span>
              <input
                type="date"
                value={cfg.launch_start || ""}
                onChange={(e) => setCfg({ ...cfg, launch_start: e.target.value })}
                className="w-40 bg-[#FAFAFA] rounded-lg px-3 py-2 text-[#1A1A1A] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
                data-testid="cfg-launch-start"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">
                Launch Partner ends (auto-cutoff)
              </span>
              <input
                type="date"
                value={cfg.launch_end || ""}
                onChange={(e) => setCfg({ ...cfg, launch_end: e.target.value })}
                className="w-40 bg-[#FAFAFA] rounded-lg px-3 py-2 text-[#1A1A1A] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
                data-testid="cfg-launch-end"
              />
            </label>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button
              type="button"
              onClick={saveConfig}
              disabled={!cfgDirty || cfgSaving}
              className="px-4 py-2 rounded-lg bg-[#1A2E35] text-white text-[13px] font-medium hover:bg-[#0f1f24] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cfg-save"
            >
              {cfgSaving ? "Saving…" : "Save pricing"}
            </button>
            {cfgDirty && (
              <span className="text-[11px] text-[#B45309]" data-testid="cfg-dirty-note">
                Unsaved changes
              </span>
            )}
            {cfgSaved?.updated_at && (
              <span className="text-[11px] text-[#6B7280] ml-auto">
                Last saved: {new Date(cfgSaved.updated_at).toLocaleString()}
                {cfgSaved.updated_by ? ` · ${cfgSaved.updated_by}` : ""}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Grid — one row per format, one column per island. Cell click
          opens an inline edit panel below the grid so the admin can
          set a status + private note without losing the grid context. */}
      <div className="card-soft p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[12px]" data-testid="sponsor-slots-grid">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#EEEEEE]">
                <th className="py-2 pr-3">Format</th>
                {ISLANDS.map((isl) => (
                  <th key={isl} className="py-2 pr-3">
                    <span className="mr-1" aria-hidden>{ISLAND_META[isl].flag}</span>
                    {ISLAND_META[isl].name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FORMATS.map((fmt) => (
                <tr key={fmt} className="border-b border-[#F5F5F5]">
                  <td className="py-2 pr-3 text-[#1A1A1A] whitespace-nowrap">
                    {FORMAT_LABEL[fmt].split(" · ")[0]}
                  </td>
                  {ISLANDS.map((isl) => {
                    const cell = findSlot(isl, fmt);
                    const isSold = cell.status === "sold";
                    let daysLeft = null;
                    if (isSold && cell.term_end_date) {
                      const end = new Date(cell.term_end_date + "T00:00:00Z");
                      daysLeft = Math.ceil((end - new Date()) / 86400000);
                    }
                    const endingSoon = isSold && daysLeft != null && daysLeft <= 14;
                    return (
                      <td key={isl} className="py-2 pr-3">
                        <button
                          type="button"
                          onClick={() => openEdit(isl, fmt)}
                          className={`inline-flex flex-col items-start text-[11px] font-medium px-2.5 py-1 rounded-lg transition-colors hover:ring-2 hover:ring-[#0AADA0]/40 ${
                            isSold
                              ? endingSoon
                                ? "bg-[#FEF3C7] text-[#92400E]"
                                : "bg-[#F5F5F5] text-[#6B7280]"
                              : "bg-[#0AADA0]/15 text-[#0AADA0]"
                          }`}
                          data-testid={`slot-cell-${isl}-${fmt}`}
                          title={
                            isSold
                              ? `${cell.sponsor_name || "Reserved"}${cell.term_end_date ? ` · ends ${cell.term_end_date}` : ""}`
                              : "Available"
                          }
                        >
                          <span>{isSold ? "Reserved" : "Available"}</span>
                          {isSold && daysLeft != null && (
                            <span className="text-[9px] opacity-75" data-testid={`slot-cell-${isl}-${fmt}-days`}>
                              {daysLeft > 0 ? `${daysLeft}d left` : `ended ${Math.abs(daysLeft)}d ago`}
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Inline edit panel — appears below the grid when an admin
          clicks a cell. Kept minimal per the spec (manual toggle;
          automated inventory system is a later phase). */}
      {editing && (
        <div className="card-soft p-5" data-testid="slot-edit-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                Editing
              </div>
              <div className="text-[15px] text-[#1A1A1A] font-medium">
                {ISLAND_META[editing.island].flag} {ISLAND_META[editing.island].name}
                {" · "}
                {FORMAT_LABEL[editing.format]}
              </div>
              {findSlot(editing.island, editing.format).sold_at && (
                <div className="text-[11px] text-[#6B7280] mt-1" data-testid="slot-edit-sold-at">
                  {findSlot(editing.island, editing.format).sponsor_name && (
                    <>
                      Sponsor: <b>{findSlot(editing.island, editing.format).sponsor_name}</b>
                      {" · "}
                    </>
                  )}
                  Sold at: {new Date(findSlot(editing.island, editing.format).sold_at).toLocaleString()}
                  {findSlot(editing.island, editing.format).sold_price_usd != null && (
                    <>
                      {" · "}
                      Locked-in rate:{" "}
                      <b data-testid="slot-edit-sold-price">
                        ${findSlot(editing.island, editing.format).sold_price_usd}
                      </b>
                      {(() => {
                        const cell = findSlot(editing.island, editing.format);
                        const idx = cell.sold_slot_index;
                        const disc = cell.sold_discount_pct;
                        const lp = cell.sold_launch_partner;
                        const parts = [];
                        if (idx) {
                          const ord = idx === 1 ? "1st" : idx === 2 ? "2nd" : idx === 3 ? "3rd" : `${idx}th`;
                          parts.push(`${ord} slot`);
                        }
                        if (disc) parts.push(`${disc}% multi-slot discount`);
                        parts.push(lp ? "Launch Partner" : "Standard");
                        return parts.length ? ` (${parts.join(", ")})` : "";
                      })()}
                    </>
                  )}
                  {findSlot(editing.island, editing.format).term_end_date && (() => {
                    const cell = findSlot(editing.island, editing.format);
                    const end = new Date(cell.term_end_date + "T00:00:00Z");
                    const now = new Date();
                    const days = Math.ceil((end - now) / 86400000);
                    return (
                      <>
                        {" · "}
                        Term:{" "}
                        <b data-testid="slot-edit-term-info">
                          {cell.term_months} months
                        </b>
                        {" · ends "}
                        <b>{cell.term_end_date}</b>
                        {" "}
                        <span className={days <= 14 ? "text-[#B45309]" : "text-[#0AADA0]"}>
                          ({days > 0 ? `${days} days left` : `${Math.abs(days)} days ago`})
                        </span>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={cancelEdit}
              className="text-[12px] text-[#6B7280] hover:text-[#1A1A1A]"
              data-testid="slot-edit-cancel"
            >
              Cancel
            </button>
          </div>

          {/* Sponsor identity — required for multi-slot discount
              grouping. Quick-picks let the admin re-use an existing
              sponsor so "Oyster Bay" and "oyster bay" don't turn into
              two different sponsors for discount counting. */}
          <div className="mb-4">
            <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
              Sponsor name (required to mark sold — this is the discount grouping key)
            </label>
            <input
              type="text"
              value={editSponsor}
              onChange={(e) => setEditSponsor(e.target.value)}
              placeholder="e.g. Oyster Bay Resort"
              className="w-full bg-[#FAFAFA] rounded-lg px-3 py-2 text-[#1A1A1A] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
              data-testid="slot-edit-sponsor-input"
            />
            {sponsors.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="slot-edit-sponsor-picks">
                <span className="text-[10px] uppercase tracking-wider text-[#6B7280] self-center mr-1">
                  Existing:
                </span>
                {sponsors.map((s) => (
                  <button
                    type="button"
                    key={s.sponsor_id}
                    onClick={() => setEditSponsor(s.sponsor_name)}
                    className={`text-[11px] px-2 py-0.5 rounded-full transition-colors ${
                      editSponsor.trim().toLowerCase() === s.sponsor_name.toLowerCase()
                        ? "bg-[#0AADA0]/15 text-[#0AADA0] ring-1 ring-[#0AADA0]"
                        : "bg-[#F5F5F5] text-[#6B7280] hover:bg-[#EEEEEE]"
                    }`}
                    data-testid={`slot-edit-sponsor-pick-${s.sponsor_id}`}
                    title={`${s.slot_count} slot${s.slot_count === 1 ? "" : "s"} — next would be ${s.slot_count + 1}${s.slot_count >= 1 ? " (25% off)" : ""}`}
                  >
                    {s.sponsor_name} · {s.slot_count}
                  </button>
                ))}
              </div>
            )}
            {(() => {
              // Live discount preview for the admin — computed from
              // the exact same rule the backend applies, so what the
              // admin sees before saving = what gets snapshotted.
              if (!editSponsor.trim() || !cfg || !pricing) return null;
              const sameCount = slots.filter(
                (x) => x.status === "sold"
                       && x.sponsor_name
                       && x.sponsor_name.trim().toLowerCase() === editSponsor.trim().toLowerCase()
                       && !(x.island === editing.island && x.format === editing.format)
              ).length;
              const idx = sameCount + 1;
              const lp = pricing.launch_partner_active;
              const base = lp ? cfg.launch_price_usd : cfg.standard_price_usd;
              const disc = idx >= 2 ? 25 : 0;
              const final = Math.round(base * (100 - disc)) / 100;
              const ord = idx === 1 ? "1st" : idx === 2 ? "2nd" : idx === 3 ? "3rd" : `${idx}th`;
              return (
                <div className="text-[11px] text-[#0AADA0] mt-2" data-testid="slot-edit-price-preview">
                  Will charge: <b>${final}</b>
                  {" "}({ord} slot for this sponsor
                  {disc ? `, ${disc}% multi-slot discount off $${base}` : `, full ${lp ? "Launch Partner" : "Standard"} rate`})
                </div>
              );
            })()}
          </div>

          <div className="mb-4">
            <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
              Term commitment (minimum 3 months — floor of the paid model)
            </label>
            <div className="flex flex-wrap gap-2">
              {[3, 6, 12, 24].map((m) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => setEditTermMonths(m)}
                  className={`text-[13px] px-3 py-1.5 rounded-full transition-colors ${
                    Number(editTermMonths) === m
                      ? "bg-[#0AADA0]/15 text-[#0AADA0] ring-2 ring-[#0AADA0]"
                      : "bg-[#FAFAFA] text-[#6B7280] hover:bg-[#F0F0F0]"
                  }`}
                  data-testid={`slot-edit-term-${m}`}
                >
                  {m} months
                </button>
              ))}
            </div>
            {(() => {
              // Live preview of the computed term_end_date so admin
              // sees the real cutoff before saving.
              const months = Math.max(3, Number(editTermMonths) || 3);
              const now = new Date();
              const end = new Date(now.getFullYear(), now.getMonth() + months, now.getDate());
              const iso = end.toISOString().slice(0, 10);
              return (
                <div className="text-[11px] text-[#0AADA0] mt-2" data-testid="slot-edit-term-preview">
                  Term end (preview): <b>{iso}</b>
                </div>
              );
            })()}
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
              Private admin note (visible only in admin — e.g. contract ref, campaign period)
            </label>
            <input
              type="text"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              placeholder="e.g. Q4 campaign · Oct-Dec 2026"
              className="w-full bg-[#FAFAFA] rounded-lg px-3 py-2 text-[#1A1A1A] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
              data-testid="slot-edit-note-input"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              onClick={() => setStatus(editing.island, editing.format, "sold", editNote, editSponsor, editTermMonths)}
              disabled={saving || !editSponsor.trim()}
              className="px-4 py-2 rounded-lg bg-[#1A2E35] text-white text-[13px] font-medium hover:bg-[#0f1f24] disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="slot-edit-mark-sold"
            >
              {saving ? "Saving…" : "Mark Sold"}
            </button>
            <button
              type="button"
              onClick={() => setStatus(editing.island, editing.format, "available", "", "", 3)}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#0AADA0]/10 text-[#0AADA0] text-[13px] font-medium hover:bg-[#0AADA0]/20 disabled:opacity-60"
              data-testid="slot-edit-mark-available"
            >
              Mark Available
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
